import { Download } from "lucide-react";
import { useInstallPrompt } from "@/lib/useInstallPrompt";
import { useLanguage } from "@/i18n/language";

/**
 * In-page "Install app" button.
 *
 * The browser fires `beforeinstallprompt` once per origin per session and only
 * when the user actually qualifies (HTTPS, manifest, service worker). Without
 * this button the offer lives in the address bar — invisible to most karigars
 * and counter staff. With it, "install" is reachable where they sign in.
 *
 * The button does nothing when the browser is not offering the install (iOS
 * Safari, already installed, embedded webview). Showing a dead button would be
 * worse than not showing it.
 */
export function InstallAppButton({ label = "Install app" }: { label?: string }) {
  const { available, installed, prompt } = useInstallPrompt();
  const { t } = useLanguage();

  // Already installed, or the browser has no offer in hand: no button at all.
  // A button that cannot act is worse than no button.
  if (!available || installed) return null;

  async function handleClick(): Promise<void> {
    const outcome = await prompt();
    /*
     * `prompt()` resolves "unavailable" when the offer it was holding had
     * already been spent — Chrome gives one event and never repeats it. The
     * button would then sit there looking healthy while doing nothing, which
     * is the worst possible outcome: the user trusts it and it silently
     * fails. Say so instead, and point at the browser's own control.
     */
    if (outcome === "unavailable") {
      window.alert(
        t(
          "ऐप इंस्टॉल करने के लिए ब्राउज़र मेन्यू (⋮) खोलें और 'ऐप इंस्टॉल करें' या 'होम स्क्रीन पर जोड़ें' चुनें।",
          "To install: open your browser menu (⋮) and choose “Install app” or “Add to Home screen”.",
        ),
      );
    }
  }

  return (
    <button
      className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-rule bg-raised px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-ink/[0.04]"
      onClick={() => {
        void handleClick();
      }}
      type="button"
    >
      <Download aria-hidden="true" className="size-4 text-brand" />
      {label}
    </button>
  );
}
